import React, { useEffect, useState } from "react";
import BlackHole from "@/components/BlackHole";
import Counter from "@/components/Counter";
import MessageModal from "@/components/MessageModal";
import { getCounter } from "@/lib/api";

export default function Home() {
  const [counter, setCounter] = useState({ count: 0, target: 1000000, event_started: false });
  const [modalOpen, setModalOpen] = useState(() =>
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("open") === "1"
  );
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const c = await getCounter();
        if (alive) setCounter(c);
      } catch (e) {
        console.error(e);
      }
    };
    load();
    const id = setInterval(load, 6000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    document.body.classList.add("void-lock");
    return () => document.body.classList.remove("void-lock");
  }, []);

  const openModal = () => {
    setEntering(true);
    // Enter transition: content dissolves, then the question appears.
    setTimeout(() => {
      setModalOpen(true);
    }, 520);
  };

  const closeModal = () => {
    setModalOpen(false);
    // Give the modal time to unmount, then release the home content
    setTimeout(() => setEntering(false), 60);
  };

  const intensity = 0.85 + Math.min(counter.count / counter.target, 1) * 1.2;
  const focusing = entering || modalOpen;

  return (
    <div
      className="relative w-full overflow-hidden"
      data-testid="home-root"
      style={{ background: "#000", height: "100dvh", minHeight: "-webkit-fill-available" }}
    >
      <BlackHole intensity={intensity} focusing={focusing} />

      {/* Vignette top/bottom */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 2,
          pointerEvents: "none",
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.9) 0%, transparent 22%, transparent 78%, rgba(0,0,0,0.9) 100%)",
        }}
      />

      {/* Wraps everything except the WebGL canvas — fades away when entering the modal */}
      <div
        className="fixed inset-0 z-10 flex flex-col"
        style={{
          opacity: focusing ? 0 : 1,
          filter: focusing ? "blur(8px)" : "blur(0px)",
          transform: focusing ? "scale(0.985)" : "scale(1)",
          transition:
            "opacity 520ms cubic-bezier(0.19,1,0.22,1), filter 520ms cubic-bezier(0.19,1,0.22,1), transform 520ms cubic-bezier(0.19,1,0.22,1)",
          pointerEvents: focusing ? "none" : "auto",
        }}
      >
        {/* Top mark — kept minimal, no decorative nav */}
        <header className="flex items-center justify-between px-6 sm:px-10 py-6 drift-in">
          <div
            className="font-mono-void text-white/70"
            style={{
              fontSize: "12px",
              letterSpacing: "0.42em",
              textTransform: "uppercase",
            }}
            data-testid="void-mark"
          >
            The Void
          </div>
          <div />
        </header>

        {/* Center content */}
        <main className="flex-1 flex flex-col items-center justify-center px-6">
          <Counter count={counter.count} target={counter.target} />

          <div className="mt-14 sm:mt-16 flex flex-col items-center gap-8 sm:gap-10 drift-in drift-delay-3">
            <div
              className="text-center max-w-md drift-in drift-delay-4"
              data-testid="hero-copy"
            >
              <div
                className="font-serif-body text-white/75"
                style={{
                  fontSize: "clamp(18px, 2vw, 22px)",
                  lineHeight: 1.55,
                }}
              >
                <div>A fear.</div>
                <div>A desire.</div>
                <div>A rage.</div>
                <div>A regret.</div>
                <div className="mt-3 text-white/85">Throw it into the darkness.</div>
              </div>
              <div
                className="mt-6 font-mono-void text-white/35"
                style={{
                  fontSize: "10px",
                  letterSpacing: "0.5em",
                  textTransform: "uppercase",
                }}
              >
                One dollar
              </div>
            </div>

            <button
              onClick={openModal}
              className="void-cta"
              data-testid="feed-the-void-btn"
              aria-label="Send a message into the void for one US dollar"
            >
              Feed the Void · $1
            </button>
          </div>
        </main>

        {/* Bottom mystery line */}
        <footer className="flex items-center justify-center px-6 sm:px-10 py-6 drift-in drift-delay-4">
          <div
            className="font-mono-void text-white/45 text-center"
            style={{
              fontSize: "10px",
              letterSpacing: "0.5em",
              textTransform: "uppercase",
            }}
            data-testid="mystery-line"
          >
            Something happens at{" "}
            <span className="text-white/75">1,000,000</span>
          </div>
        </footer>
      </div>

      <MessageModal open={modalOpen} onClose={closeModal} />
    </div>
  );
}
